import { uniqBy } from 'lodash-es';
import { mapActions, mapState, mapGetters } from 'vuex';
import InsightsTracking from './InsightsTrackingMixin';
import { makeUserTimeZone } from '~/utils/time_utils';

// Constants for tab names to avoid magic strings
const TABS = {
  TIMELINE: 'timeline',
  UNUSUAL_ACTIVITIES: 'unusual-activities',
};

export default {
  mixins: [InsightsTracking],
  data: () => ({
    filterSettings: Object.freeze({
      allowClear: false,
      multiple: false,
      placeholder: 'Loading...',
      minimumResultsForSearch: 8,
    }),
    dateFilterTimeout: null,
  }),
  watch: {
    users(val) {
      if (val.length) {
        this.populateTimeZoneOptions();
      }
    },
  },
  computed: {
    ...mapState(['organization', 'filters', 'previousView', 'current_user_time_zone']),
    ...mapGetters([
      'selectedFilter',
      'isEverythingReady',
      'users',
      'teams',
      'timeZoneOptions',
      'activeTimeZone',
      'dashboardType',
      'isOwner',
      'isTeamLead',
      'isManager',
      'previousDashboardIsIndividualTeam',
      'currentUserBelongsToPrevTeam',
      'previousDashboardTeamName',
    ]),
    backButtonText() {
      let text = 'All members';

      if (this.previousDashboardIsIndividualTeam && this.currentUserBelongsToPrevTeam) {
        text = this.previousDashboardTeamName;
      }

      return text;
    },
    /**
     * Determines if widget reloading should be skipped for filter changes.
     * Currently, the timeline tab manages its own widget loading state,
     * so we skip global widget reload to prevent UI conflicts.
     */
    shouldSkipWidgetReload() {
      return this.filters.tab === TABS.TIMELINE;
    },
  },
  methods: {
    ...mapActions([
      'setTimeZoneOptions',
      'setFilter',
      'setShowBenchmarksAverages',
      'fetchEngagementScores',
      'setPreviousView',
      'setSelectedFilter',
    ]),
    setDateFilter({ query }) {
      // Clear any existing timeout
      if (this.dateFilterTimeout) {
        clearTimeout(this.dateFilterTimeout);
      }

      // Debounce the global filter updates and API calls.
      this.dateFilterTimeout = setTimeout(() => {
        // Skip widget reload on timeline tab since it manages its own loading state, and on
        // initial mount to prevent double-fetching with TheInsights created hook. The selected
        // timezone is preserved during initial hydration (resetTimeZone: false).
        this.applyScopeChange({ date: query.value }, {
          resetTimeZone: !query.initial,
          skipWidgets: query.initial,
        });

        // Only fetch engagement scores when navigation sidebar is visible
        // (engagement scores are only displayed in the navigation sidebar, which is hidden on timeline tab)
        if (this.filters.tab !== TABS.TIMELINE) {
          this.fetchEngagementScores();
        }
      }, 300);

      // Analytics tracking (keep immediate for accurate user interaction tracking)
      // Skip tracking when:
      // 1. query.initial is true (initial load)
      // 2. query.skipTracking is true (implicit date change from member/team selection)
      if (!query.initial && !query.skipTracking) {
        const to = new Date(query.value.to).getTime();
        const from = new Date(query.value.from).getTime();

        this.trackAnalytics('Insights date apply', {
          days_in_range: (to - from) / (1000 * 3600 * 24),
          preset_value: query.value.id,
          type: 'Date picker',
          statsigCustomIDs: ['organizationID', this.organization.id],
        });
      }
    },

    setMembersWithPreviousView(string, teamId = null) {
      // A cleared selection (null/undefined) means All Members.
      const scope = string ?? 'all_user_id';

      this.setSelectedFilter(scope);
      this.setPreviousView({ previousView: teamId || this.selectedFilter, currentView: scope });
      // Give time to the team filter to settle setting the members filter
      this.$nextTick(() => {
        this.setMembersFilter(scope);
      });
    },

    setMembersFilter(member) {
      const memberScope = member ?? 'all_user_id';
      let initialValue;
      let initialFilterType;

      if (this.filters.user_id) {
        initialValue = String(this.filters.user_id);
        initialFilterType = 'user';
      } else if (this.filters.team_id) {
        initialValue = String(this.filters.team_id);
        initialFilterType = 'team';
      } else {
        initialValue = initialFilterType = 'all';
      }

      const [id, prefix] = [...memberScope.split(/_(.*)/s)];
      const opposite = prefix === 'user_id' ? 'team_id' : 'user_id';

      // The member-scoped timezone option only exists for a single user; applyScopeChange refreshes
      // the options for the new scope and resets the selection if it became invalid.
      this.applyScopeChange({ [prefix]: id, [opposite]: null });

      let event;

      switch (this.filters.tab) {
        case TABS.UNUSUAL_ACTIVITIES:
          event = 'Unusual filter changed';
          break;
        case TABS.TIMELINE:
          event = 'Timeline filter changed';
          break;
        default:
          event = 'Insights filters changed';
      }

      this.trackAnalytics(event, {
        initial_value: initialValue,
        initial_filter_type: initialFilterType,
        new_value: id,
        filter_type: id === 'all' ? 'all' : prefix.split('_')[0],
      });
    },
    // Reads the applied filters by default; callers pass overrides to evaluate a pending change
    // (new date/member) before it is written, so the timezone options can be batched in one apply.
    timeZoneOptionsForCurrentFilter({ date = this.filters?.date, userId = this.filters?.user_id } = {}) {
      if (!this.users?.length) {return;}

      const from = date?.from ? moment(date.from) : null;
      const to = date?.to ? moment(date.to) : null;
      const isDayPeriod = from && to ? from.isSame(to, 'day') : false;
      const user = this.users.find((u) => u.id === +userId);

      if (isDayPeriod && user) {
        const userTimeZone = typeof user.time_zone === 'string'
          ? makeUserTimeZone(user.name, user.time_zone)
          : user.time_zone;
        const options = [this.organization.time_zone, userTimeZone, this.current_user_time_zone];

        return uniqBy(options.filter(Boolean), 'for');
      }

      return [this.organization.time_zone];
    },
    // Sets the timezone dropdown options when a set was resolved (no-op when none), so the callers
    // below do not each re-implement the presence check.
    applyTimeZoneOptions(options) {
      if (options) {
        this.setTimeZoneOptions(options);
      }
    },
    // tz_options drives the dropdown only - never use it to validate filters.time_zone.
    populateTimeZoneOptions() {
      this.applyTimeZoneOptions(this.timeZoneOptionsForCurrentFilter());
    },
    // Returns the timezone to fall back to when the applied one is not among the given options,
    // or null when the current selection is still valid.
    invalidTimeZoneReset(options) {
      const current = this.filters?.time_zone;

      if (!options || !current || options.some((tz) => tz.for === current)) {
        return null;
      }

      return this.organization.time_zone.for;
    },

    // Applies a partial change to the shared filters in a single setFilter, refreshing the timezone
    // options for the resulting scope and (unless resetTimeZone is false) resetting the selected
    // timezone in the same write when it is no longer valid for that scope.
    applyScopeChange(changes, { resetTimeZone = true, skipWidgets = false } = {}) {
      const pending = { ...this.filters, ...changes };
      const options = this.timeZoneOptionsForCurrentFilter({ date: pending.date, userId: pending.user_id });

      this.applyTimeZoneOptions(options);
      const timeZoneReset = resetTimeZone ? this.invalidTimeZoneReset(options) : null;

      this.setFilter({
        changes: { ...changes, ...(timeZoneReset ? { time_zone: timeZoneReset } : {}) },
        skipWidgets: skipWidgets || this.shouldSkipWidgetReload,
      });
    },

    changeTimeZone(tzParam) {
      if (this.filters.time_zone !== tzParam) {
        this.setFilter({
          changes: { time_zone: tzParam },
          skipWidgets: this.shouldSkipWidgetReload,
        });
      }
    },

    setConfidenceLevelsFilter(confidenceLevels) {
      this.setFilter({ changes: { confidence_levels: confidenceLevels } });
    },

    backButtonLink() {
      let string = 'all_user_id';

      if (this.previousDashboardIsIndividualTeam && this.currentUserBelongsToPrevTeam) {
        string = this.previousView;
      }

      this.setPreviousView({ previousView: null, currentView: string });
      this.setSelectedFilter(string);
      this.setMembersFilter(string);
    },
  },
};
